import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FileText, Trash2, AlertCircle, Plus, Youtube } from "lucide-react";
import Input from "../common/Input";
import Button from "../common/Button";
import CustomSelect from "../common/CustomSelect";
import FileUpload from "../common/FileUpload";
import toast from "react-hot-toast";
import {
  createEvent,
  getEventById,
  updateEvent,
} from "../../../services/eventsServices";
import { getAllCategories } from "../../../services/categoryServices";
import { RichTextEditor } from "../common/RichTextEditor";
import { deleteImage } from "../../../services/uploaderServices";

const statusOptions = [
  { value: "active", label: "Active" },
  { value: "in-active", label: "Inactive" },
];

const homepageOptions = [
  { value: "yes", label: "Yes" },
  { value: "no", label: "No" },
];

const YOUTUBE_REGEX = /^https?:\/\/(www\.)?(youtube\.com|youtu\.be)\//;
const isValidYouTubeUrl = (url) => YOUTUBE_REGEX.test(url);

// ─── Reusable error component ─────────────────────────────────────────────────
const FieldError = ({ message }) => (
  <p className="text-red-600 font-semibold text-xs mt-1 flex items-center gap-1">
    <AlertCircle size={12} />
    {message}
  </p>
);

const EditEventForms = () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const eventId = queryParams.get("eventId");
  const navigate = useNavigate();

  const presentationRef = useRef();
  const imageRef = useRef();
  const galleryRef = useRef();
  const mainImageRef = useRef();

  const [presentations, setPresentations] = useState([]);
  const [title, setTitle] = useState("");
  const [date, setDate] = useState("");
  const [showOnHomepage, setShowOnHomepage] = useState(null);
  const [description, setDescription] = useState("");
  const [status, setStatus] = useState(null);
  const [category, setCategory] = useState(null);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [image, setImage] = useState("");
  const [mainImage, setMainImage] = useState("");
  const [galleryImages, setGalleryImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [resources, setResources] = useState([{ label: "", link: "" }]);

  const [errors, setErrors] = useState({});

  // ─── Fetch Categories ─────────────────────────────────────────────
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await getAllCategories();
        setCategoryOptions(res.data || []);
      } catch {
        toast.error("Failed to load categories");
      }
    };
    fetchCategories();
  }, []);

  // ─── Fetch Event ──────────────────────────────────────────────────
  const fetchEvent = async () => {
    try {
      const res = await getEventById(eventId);
      const data = res.data;

      setTitle(data.title || "");
      setDate(data.date ? data.date.split("T")[0] : "");
      setDescription(data.description || "");
      setStatus(
        data.status
          ? statusOptions.find((opt) => opt.value === data.status)
          : null,
      );

      const selectedCategory = categoryOptions.find(
        (opt) => opt.value === data.category._id,
      );
      setCategory(selectedCategory || null);

      setShowOnHomepage(
        data.showOnHomepage
          ? homepageOptions.find((opt) => opt.value === "yes")
          : homepageOptions.find((opt) => opt.value === "no"),
      );

      setImage(data.image || "");
      setMainImage(data.mainImage || "");
      setGalleryImages(data.galleryImages || []);
      setPresentations(data.presentations || []);

      // Populate resources — fallback to one empty row if none exist
      setResources(
        data.resources?.length > 0 ? data.resources : [{ label: "", link: "" }],
      );
    } catch (error) {
      toast.error(error.message || "Failed to fetch event");
    }
  };

  useEffect(() => {
    if (!eventId || categoryOptions.length === 0) return;
    fetchEvent();
  }, [eventId, categoryOptions]);

  // ─── Resources Handlers ───────────────────────────────────────────
  const handleAddResource = () => {
    setResources((prev) => [...prev, { label: "", link: "" }]);
  };

  const handleRemoveResource = (index) => {
    setResources((prev) => prev.filter((_, i) => i !== index));
    setErrors((prev) => {
      const updated = { ...prev };
      delete updated[`resource_label_${index}`];
      delete updated[`resource_link_${index}`];
      return updated;
    });
  };

  const handleResourceChange = (index, field, value) => {
    setResources((prev) =>
      prev.map((r, i) => (i === index ? { ...r, [field]: value } : r)),
    );
    if (errors[`resource_${field}_${index}`]) {
      setErrors((prev) => ({ ...prev, [`resource_${field}_${index}`]: "" }));
    }
  };

  // ─── Validation ───────────────────────────────────────────────────
  const validate = () => {
    const newErrors = {};

    if (!title) newErrors.title = "Title is required";
    if (!date) newErrors.date = "Date is required";
    if (!description) newErrors.description = "Description is required";
    if (!showOnHomepage) newErrors.showOnHomepage = "Please select an option";
    if (!category) newErrors.category = "Please select a category";
    if (!image) newErrors.image = "Tile image is required";
    if (!mainImage) newErrors.mainImage = "Main image is required";

    resources.forEach((r, i) => {
      const hasLabel = r.label.trim() !== "";
      const hasLink = r.link.trim() !== "";

      if (hasLabel || hasLink) {
        if (!hasLabel) newErrors[`resource_label_${i}`] = "Label is required";
        if (!hasLink) {
          newErrors[`resource_link_${i}`] = "YouTube link is required";
        } else if (!isValidYouTubeUrl(r.link)) {
          newErrors[`resource_link_${i}`] = "Must be a valid YouTube URL";
        }
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ─── Build Payload ────────────────────────────────────────────────
  const buildPayload = () => ({
    title,
    description,
    date,
    showOnHomepage: showOnHomepage?.value === "yes",
    status: status ? status.value : "active",
    category: category?.value,
    image,
    mainImage,
    approvalStatus: "approved",
    presentations: presentations.map((p) => ({ title: p.title, url: p.url })),
    galleryImages: galleryImages || [],
    resources: resources.filter(
      (r) => r.label.trim() !== "" && r.link.trim() !== "",
    ),
  });

  // ─── Submit ───────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!validate()) return;

    const payload = buildPayload();

    try {
      setLoading(true);
      if (eventId) {
        await updateEvent(eventId, payload);
        toast.success("Event updated successfully!");
      } else {
        await createEvent(payload);
        toast.success("Event created successfully!");
      }
      navigate("/admin/events/list");
    } catch (error) {
      toast.error(error.message || "Failed to save event");
    } finally {
      setLoading(false);
    }
  };

  // ─── Presentation Handlers ────────────────────────────────────────
  const handlePresentationUpload = (uploadResult) => {
    if (uploadResult?.success && uploadResult?.results?.length > 0) {
      const newPresentations = uploadResult.results.map((result) => ({
        url: result.documentUrl,
        title: result.title,
      }));
      setPresentations((prev) => [...prev, ...newPresentations]);
    } else {
      toast.error("Upload failed");
    }
  };

  const handleDeletePresentation = async (index, item) => {
    const oldPresentations = [...presentations];
    const updatedPresentations = presentations.filter((_, i) => i !== index);
    setPresentations(updatedPresentations);

    try {
      await deleteImage(item.url);

      const payload = buildPayload();
      payload.presentations = updatedPresentations.map((p) => ({
        title: p.title,
        url: p.url,
      }));
      await updateEvent(eventId, payload);
      toast.success("Presentation deleted successfully");
    } catch (error) {
      setPresentations(oldPresentations);
      toast.error(error.message || "Failed to delete presentation");
    }
  };

  // ─── Image Delete Handlers ────────────────────────────────────────
  const handleMainImageDelete = async (updatedValue) => {
    try {
      const payload = buildPayload();
      payload.mainImage = updatedValue;
      await updateEvent(eventId, payload);
    } catch (error) {
      toast.error(error.message || "Failed to update main image");
      throw error;
    }
  };

  const handleTileImageDelete = async (updatedValue) => {
    try {
      const payload = buildPayload();
      payload.image = updatedValue;
      await updateEvent(eventId, payload);
    } catch (error) {
      toast.error(error.message || "Failed to update tile image");
      throw error;
    }
  };

  const handleGalleryDelete = async (updatedGallery) => {
    const payload = buildPayload();
    payload.galleryImages = updatedGallery;
    await updateEvent(eventId, payload);
  };

  // ─── Render ───────────────────────────────────────────────────────
  return (
    <div className="bg-[#F5F0FF] p-6 rounded-lg font-sans space-y-4">
      {/* Row 1: Title, Date, Show on Homepage */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <Input
            label="Title"
            value={title}
            onChange={(val) => {
              setTitle(val);
              if (errors.title) setErrors((prev) => ({ ...prev, title: "" }));
            }}
            required
          />
          {errors.title && <FieldError message={errors.title} />}
        </div>

        <div>
          <Input
            label="Date"
            type="date"
            value={date}
            onChange={(val) => {
              setDate(val);
              if (errors.date) setErrors((prev) => ({ ...prev, date: "" }));
            }}
            required
          />
          {errors.date && <FieldError message={errors.date} />}
        </div>

        <div>
          <CustomSelect
            label="Show On Homepage"
            value={showOnHomepage}
            onChange={(val) => {
              setShowOnHomepage(val);
              if (errors.showOnHomepage)
                setErrors((prev) => ({ ...prev, showOnHomepage: "" }));
            }}
            options={homepageOptions}
            placeholder="Yes / No"
          />
          {errors.showOnHomepage && (
            <FieldError message={errors.showOnHomepage} />
          )}
        </div>
      </div>

      {/* Description */}
      <div>
        <RichTextEditor
          description={description}
          setDescription={(val) => {
            setDescription(val);
            if (errors.description)
              setErrors((prev) => ({ ...prev, description: "" }));
          }}
        />
        {errors.description && <FieldError message={errors.description} />}
      </div>

      {/* Row 2: Status, Category */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <CustomSelect
          label="Status"
          value={status}
          onChange={setStatus}
          options={statusOptions}
        />
        <div>
          <CustomSelect
            label="Category"
            value={category}
            onChange={(val) => {
              setCategory(val);
              if (errors.category)
                setErrors((prev) => ({ ...prev, category: "" }));
            }}
            options={categoryOptions}
            placeholder="Select Category"
          />
          {errors.category && <FieldError message={errors.category} />}
        </div>
      </div>

      {/* Row 3: Images, Gallery, Presentations */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div>
          <FileUpload
            ref={mainImageRef}
            single={true}
            mode="main"
            onUpload={(res) => {
              if (res?.success && res.images?.length) {
                setMainImage(res.images[0].imageUrl);
                if (errors.mainImage)
                  setErrors((prev) => ({ ...prev, mainImage: "" }));
              } else {
                toast.error("Main image upload failed");
              }
            }}
            onDelete={handleMainImageDelete}
            defaultLabel="Choose Main Image"
            image={mainImage ? [mainImage] : []}
            setImage={(imgs) => {
              setMainImage(imgs[0] || "");
              if (errors.mainImage)
                setErrors((prev) => ({ ...prev, mainImage: "" }));
            }}
          />
          {errors.mainImage && <FieldError message={errors.mainImage} />}
        </div>

        <div>
          <FileUpload
            ref={imageRef}
            single={true}
            mode="tile"
            onUpload={(res) => {
              if (res?.success && res.images?.length) {
                setImage(res.images[0].imageUrl);
                if (errors.image) setErrors((prev) => ({ ...prev, image: "" }));
              } else {
                toast.error("Tile image upload failed");
              }
            }}
            onDelete={handleTileImageDelete}
            defaultLabel="Choose Tile Image"
            image={image ? [image] : []}
            setImage={(imgs) => {
              setImage(imgs[0] || "");
              if (errors.image) setErrors((prev) => ({ ...prev, image: "" }));
            }}
          />
          {errors.image && <FieldError message={errors.image} />}
        </div>

        <div className="flex flex-col items-start gap-y-4">
          <FileUpload
            ref={galleryRef}
            mode="image"
            defaultLabel="Upload Gallery Images"
            multiple={true}
            image={galleryImages}
            setImage={setGalleryImages}
            onDelete={handleGalleryDelete}
          />
        </div>

        <div className="flex flex-col items-start gap-y-4">
          <FileUpload
            ref={presentationRef}
            mode="document"
            onUpload={handlePresentationUpload}
            defaultLabel="Upload Presentation"
            disabled={false}
          />
          {presentations.length > 0 && (
            <div className="w-full space-y-2">
              {presentations.map((item, index) => (
                <div
                  key={index}
                  className="relative bg-white p-3 rounded-md shadow-sm border border-gray-200 w-full"
                >
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex items-center gap-2">
                      <FileText
                        size={16}
                        className="text-blue-600 flex-shrink-0"
                      />
                      <span className="text-xs text-gray-500">
                        Presentation {index + 1}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleDeletePresentation(index, item)}
                      className="text-red-500 hover:text-red-700 transition-colors"
                      title="Delete presentation"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-gray-800 break-words">
                      {item.title}
                    </p>
                    <a
                      href={item.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-blue-600 hover:text-blue-800 underline break-all"
                    >
                      View Document →
                    </a>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Row 4: Resources (YouTube Links) */}
      <div className="bg-white rounded-lg p-4 border border-gray-200 space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Youtube size={18} className="text-red-500" />
            <h3 className="text-sm font-semibold text-gray-700">
              Resources{" "}
              <span className="text-gray-400 font-normal">(YouTube links)</span>
            </h3>
          </div>
          <button
            type="button"
            onClick={handleAddResource}
            className="flex items-center gap-1 text-xs text-purple-600 hover:text-purple-800 font-medium transition-colors"
          >
            <Plus size={14} />
            Add Resource
          </button>
        </div>

        {resources.map((resource, index) => (
          <div
            key={index}
            className="grid grid-cols-1 md:grid-cols-2 gap-3 items-start"
          >
            <div>
              <Input
                label={`Label ${index + 1}`}
                value={resource.label}
                onChange={(val) => handleResourceChange(index, "label", val)}
                placeholder="e.g. Keynote Recording"
              />
              {errors[`resource_label_${index}`] && (
                <FieldError message={errors[`resource_label_${index}`]} />
              )}
            </div>

            <div className="flex items-start gap-2">
              <div className="flex-1">
                <Input
                  label={`YouTube Link ${index + 1}`}
                  value={resource.link}
                  onChange={(val) => handleResourceChange(index, "link", val)}
                  placeholder="https://youtube.com/watch?v=..."
                />
                {errors[`resource_link_${index}`] && (
                  <FieldError message={errors[`resource_link_${index}`]} />
                )}
              </div>
              {resources.length > 1 && (
                <button
                  type="button"
                  onClick={() => handleRemoveResource(index)}
                  className="mt-7 text-red-500 hover:text-red-700 transition-colors flex-shrink-0"
                  title="Remove resource"
                >
                  <Trash2 size={16} />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Actions */}
      <div className="pt-4 space-y-2">
        <Button
          className="w-full"
          type="button"
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? "Submitting..." : "Confirm & Save Event"}
        </Button>
        <Button
          variant="text"
          className="w-full"
          onClick={() => navigate("/admin/events/list")}
        >
          Cancel
        </Button>
      </div>
    </div>
  );
};

export default EditEventForms;
